import { CommonModule, NgTemplateOutlet } from '@angular/common';
import { Component, inject, input, Input, signal, output } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { TranslateModule, TranslatePipe } from '@ngx-translate/core';
import { BsDaterangepickerConfig } from 'ngx-bootstrap/datepicker';
import { Highlight } from 'ngx-highlightjs';
import { AppShieldEventLogsStore, ShieldApiStore, ShieldLogsItemModel, WafRuleSeverityEnum } from '@bn/app-shield';
import { AbstractFilterDirective } from '@bn/shared/entities/abstracts/abstract-filter.directive';
import { Size, StringBooleanMap } from '@bn/shared/entities/models';
import { JsonFormatPipe } from '@bn/shared/entities/pipes';
import { StoreRequest } from '@bn/shared/state-store';
import { DateTimeFormat, DateTimeUtils } from '@bn/shared/utils';
// Note: pipe has to be imported like this instead of:
// import { WafSeverityRulePipe } from '@bn/app-shield';
// this caused an error on runtime and it was hard to find
import { BadgeComponent } from '@bn/web-ui/badge/badge.component';
import { ButtonComponent } from '@bn/web-ui/button/button.component';
import { DatePickerComponent } from '@bn/web-ui/datepicker/datepicker.component';
import { DropdownMenuItem } from '@bn/web-ui/dropdown-menu';
import { DropdownMenuComponent } from '@bn/web-ui/dropdown-menu/dropdown-menu.component';
import { FlagComponent } from '@bn/web-ui/flag/flag.component';
import { NotAvailableTemplateComponent } from '@bn/web-ui/not-available';
import { PrevNextPaginationComponent } from '@bn/web-ui/prev-next-pagination/prev-next-pagination.component';
import { TableSkeletonTemplateComponent } from '@bn/web-ui/standalone';
import { WafService } from '../../services/waf.service';
import { GetLogDropdownItemsPipe } from './pipes';

@Component({
  selector: 'app-app-shield-event-logs',
  templateUrl: './app-shield-event-logs.component.html',
  styleUrls: ['./app-shield-event-logs.component.scss'],
  imports: [
    CommonModule,
    FormsModule,
    BadgeComponent,
    ButtonComponent,
    DatePickerComponent,
    DropdownMenuComponent,
    FlagComponent,
    PrevNextPaginationComponent,
    FaIconComponent,
    Highlight,
    JsonFormatPipe,
    GetLogDropdownItemsPipe,
    NgTemplateOutlet,
    TranslateModule,
    TableSkeletonTemplateComponent,
    NotAvailableTemplateComponent,
  ],
  providers: [JsonFormatPipe, TranslatePipe],
})
export class AppShieldEventLogsComponent extends AbstractFilterDirective {
  readonly eventStore = inject(AppShieldEventLogsStore);

  readonly datePickerConfig = input<Partial<BsDaterangepickerConfig>>({
    maxDateRange: 30,
    minDate: DateTimeUtils.dayjsNow().subtract(2, 'd').toDate(),
    maxDate: DateTimeUtils.dayjsNow().toDate(),
  });
  readonly showFilter = input(false);
  readonly showPagination = input(true);
  readonly showLatestRows = input<number | null>(null);

  @Input()
  public showHeader? = true;

  @Input()
  public lastRowBorderRadius? = false;

  @Input()
  public hideLastBorder? = false;

  @Input()
  public headerSize?: Extract<Size, 'sm' | 'md'> = 'md';

  @Input()
  public notAvailableOptions?: {
    show?: boolean;
    noShadow?: boolean;
    noImage?: boolean;
    noTitle?: boolean;
    noPaddingTop?: boolean;
  };

  public readonly countUpdate = output<number>();

  readonly #shieldApiStore = inject(ShieldApiStore);
  readonly #wafService = inject(WafService);

  public isLoading = signal<boolean>(false);
  public logs = signal<ShieldLogsItemModel[] | null>(null);
  datePickerValue = DateTimeUtils.dayjsNow().toDate();
  public readonly getSelectedShieldId = this.#shieldApiStore.selectedEntityId();
  public readonly isAnyRuleUpdated = this.#shieldApiStore.isFinished(StoreRequest.Update, ['rule-*']);
  public readonly dateFormat = DateTimeFormat.DateTimeLong;

  public showDropdown = true;
  public wafRuleSeverityEnum = WafRuleSeverityEnum;
  public openRows: StringBooleanMap = {};

  public onRowToggle(index: number): void {
    this.openRows[index] = !this.openRows[index] || false;
  }

  constructor() {
    super();

    toObservable(this.getSelectedShieldId)
      .pipe(takeUntilDestroyed())
      .subscribe((shieldId) => {
        if (!shieldId) return;

        this.eventStore.setShieldId(Number(shieldId));
        this.eventStore.forceResetEntitiesFilter();
      });

    // Note: action menu on shield logs was not updated after change
    toObservable(this.isAnyRuleUpdated)
      .pipe(takeUntilDestroyed())
      .subscribe((isAnyRuleUpdated) => {
        if (!isAnyRuleUpdated && !this.getSelectedShieldId()) return;
        // use same filter as last used, just to refresh
        this.eventStore.filterEntities({ filter: this.eventStore.entitiesFilter() });
      });

    toObservable(this.eventStore.isLoading)
      .pipe(takeUntilDestroyed())
      .subscribe((isLoading) => {
        if (typeof isLoading === 'boolean') {
          this.isLoading.set(isLoading);
        }
      });

    toObservable(this.eventStore.isLoaded)
      .pipe(takeUntilDestroyed())
      .subscribe((isLoaded) => {
        if (isLoaded && this.eventStore.entities()) {
          const showOnlyCount = this.showLatestRows();
          // Overview page has only latest logs
          if (showOnlyCount) {
            this.logs.set([...this.eventStore.entities().splice(-showOnlyCount)]);
          } else {
            this.logs.set(this.eventStore.entities());
          }

          this.countUpdate.emit(this.logs()?.length ?? 0);
        }
      });
  }

  public onDropdownClick({ action, id }: DropdownMenuItem): void {
    switch (action) {
      case 'disable':
        this.#wafService.disableRules([String(id)]);
        break;

      case 'enable':
        this.#wafService.enableRules([String(id)]);
        break;

      case 'log':
        this.#wafService.logRules([String(id)]);
        break;
    }
  }

  // set store filter with new token
  public onPaginationChange(token: string | null): void {
    // Allow empty string to refresh latest page?
    if (token || token === '') this.eventStore.filterEntities({ filter: { continuationToken: token }, patch: true, forceLoad: true });
  }

  public onDatePickerChange(date: string): void {
    this.eventStore.filterEntities({ filter: { continuationToken: '', date }, patch: true });
  }

  public onButtonRefresh(): void {
    this.eventStore.filterEntities({ filter: {}, forceLoad: true, patch: true });
  }
}
